import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, RefreshCw, AlertTriangle, X, CheckCircle, Wrench } from 'lucide-react';
import MainService from '../../services/MainService';
import { mapVehicleDropdownList, isApiSaveSuccess } from '../../utils/vehicleApiUtils';

const AddMaintenance = () => {
  const { vehicleId, maintenanceId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [maintenance, setMaintenance] = useState({
    Id: 0,
    VehicleId: vehicleId || '',
    ServiceType: '',
    ServiceDate: '',
    ServiceProvider: '',
    Cost: '',
    Description: '',
    NextServiceDate: '',
    Mileage: '',
    Notes: ''
  });
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    loadVehicles();
    if (maintenanceId) {
      loadMaintenance();
    }
  }, [maintenanceId]);

  useEffect(() => {
    if (!maintenanceId && vehicleId) {
      setMaintenance((prev) => ({ ...prev, VehicleId: vehicleId }));
    }
  }, [vehicleId, maintenanceId]);

  const loadVehicles = async () => {
    try {
      const response = await MainService.VehiclesInDropDown();
      setVehicles(mapVehicleDropdownList(response));
    } catch (err) {
      console.error('Error loading vehicles:', err);
      setVehicles([]);
    }
  };

  const loadMaintenance = async () => {
    try {
      setLoading(true);
      const row = await MainService.VehicleServiceHistoryById(maintenanceId);
      if (row) {
        setMaintenance({
          Id: row.Id ?? row.ID ?? 0,
          VehicleId: String(row.VehicleId ?? row.VehicleID ?? vehicleId ?? ''),
          ServiceType: row.ServiceType || '',
          ServiceDate: row.ServiceDate || '',
          ServiceProvider: row.WorkshopName || '',
          Cost: row.Cost != null && row.Cost !== '' ? String(row.Cost) : '',
          Description: '',
          NextServiceDate: row.NextServiceDue || '',
          Mileage: '',
          Notes: row.Notes || ''
        });
      }
    } catch (err) {
      console.error('Error loading maintenance:', err);
      setError('Failed to load maintenance record');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMaintenance(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!maintenance.ServiceType) {
      setError('Please enter service type');
      return;
    }
    if (!maintenance.ServiceDate) {
      setError('Please select service date');
      return;
    }
    const vid = Number(maintenance.VehicleId || vehicleId);
    if (!vid) {
      setError('Please select vehicle');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const noteParts = [];
      if (maintenance.Description) noteParts.push(maintenance.Description);
      if (maintenance.Mileage) noteParts.push(`Mileage: ${maintenance.Mileage}`);
      if (maintenance.Notes) noteParts.push(maintenance.Notes);
      const mergedNotes = noteParts.join('\n\n');
      const payload = {
        Id: Number(maintenance.Id) || 0,
        VehicleId: vid,
        ServiceDate: maintenance.ServiceDate,
        ServiceType: maintenance.ServiceType,
        WorkshopName: maintenance.ServiceProvider || '',
        Cost: maintenance.Cost === '' || maintenance.Cost == null ? null : Number(maintenance.Cost),
        NextServiceDue: maintenance.NextServiceDate || null,
        Notes: mergedNotes || ''
      };
      const response = await MainService.SaveVehicleServiceHistory(payload);
      if (!isApiSaveSuccess(response)) {
        setError('Failed to save maintenance record');
        return;
      }
      setSuccess('Maintenance record saved successfully');
      setTimeout(() => {
        navigate(
          vehicleId
            ? `/home/vehicle-management/maintenance/${vehicleId}`
            : '/home/vehicle-management/maintenance'
        );
      }, 800);
    } catch (err) {
      console.error('Error saving maintenance:', err);
      setError('Failed to save maintenance record');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setMaintenance({
      Id: 0,
      VehicleId: vehicleId || '',
      ServiceType: '',
      ServiceDate: '',
      ServiceProvider: '',
      Cost: '',
      Description: '',
      NextServiceDate: '',
      Mileage: '',
      Notes: ''
    });
    setError('');
    setSuccess('');
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <Wrench className="w-6 h-6 text-primary mr-2" />
            <h1 className="text-3xl font-bold text-gray-900">
              {maintenanceId ? 'Edit Maintenance' : 'Add Maintenance'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleReset}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Reset</span>
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-[#46955A] text-white rounded-lg hover:bg-[#3d7d4f] transition-colors disabled:opacity-50"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>Save</span>
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500 ml-8">
          {maintenanceId ? 'Update maintenance record' : 'Create a new maintenance record'}
        </p>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Vehicle */}
          {!vehicleId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vehicle <span className="text-red-500">*</span>
              </label>
              <select
                name="VehicleId"
                value={maintenance.VehicleId}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
              >
                <option value="">Select Vehicle</option>
                {vehicles.map(vehicle => (
                  <option key={vehicle.Id} value={vehicle.Id}>
                    {vehicle.PlateNumber || vehicle.Number || vehicle.Name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Service Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service Type <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="ServiceType"
              value={maintenance.ServiceType}
              onChange={handleInputChange}
              placeholder="Enter service type"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>

          {/* Service Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="ServiceDate"
              value={maintenance.ServiceDate}
              onChange={handleInputChange}
              className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent ${!maintenance.ServiceDate ? 'text-gray-400' : 'text-gray-900'}`}
            />
          </div>

          {/* Service Provider */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Service Provider</label>
            <input
              type="text"
              name="ServiceProvider"
              value={maintenance.ServiceProvider}
              onChange={handleInputChange}
              placeholder="Enter service provider"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>

          {/* Cost */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cost <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="Cost"
              value={maintenance.Cost}
              onChange={handleInputChange}
              placeholder="Enter cost"
              step="0.01"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>

          {/* Mileage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Mileage</label>
            <input
              type="number"
              name="Mileage"
              value={maintenance.Mileage}
              onChange={handleInputChange}
              placeholder="Enter mileage"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>

          {/* Next Service Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Next Service Date</label>
            <input
              type="date"
              name="NextServiceDate"
              value={maintenance.NextServiceDate}
              onChange={handleInputChange}
              className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent ${!maintenance.NextServiceDate ? 'text-gray-400' : 'text-gray-900'}`}
            />
          </div>

          {/* Description */}
          <div className="md:col-span-2 lg:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              name="Description"
              value={maintenance.Description}
              onChange={handleInputChange}
              placeholder="Enter description"
              rows="3"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>

          {/* Notes */}
          <div className="md:col-span-2 lg:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              name="Notes"
              value={maintenance.Notes}
              onChange={handleInputChange}
              placeholder="Enter additional notes"
              rows="3"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddMaintenance;

